import { FC } from 'react';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Formik, Field as FormikField, FormikErrors, FieldProps } from 'formik';
import {
  Flex,
  Box,
  H1,
  Logo,
  Text,
  Button,
  ButtonGroup,
  Anchor,
  Alert,
  Label,
  Hide,
  Image,
  CheckboxStateless,
  AnchorButton,
  LoadableButton,
  I18NContinue,
  Input
} from '../../components';
import {
  GraphQLErrors,
  GraphQLErrorWithCode
} from '../../components/GraphQLErrors';
import { InputInlineButton } from '../../components/InputInlineButton';
import { globalMessages, genders } from '../../lib/messages';
import * as messages from './messages';
import {
  LargeChat as ChatIcon,
  LargeCalendar as CalendarIcon,
  LargeProfile as ProfileIcon,
  LargeHome as HomeIcon,
  LargeMail as MailIcon,
  Warning as WarningIcon,
  Creditcard
} from '@urbaninfrastructure/react-icons';
import {
  connectObosMembershipMutation,
  Gender,
  CurrentUser,
  CurrentUser_paymentMethod
} from '../../core-types';
import { useUpdateCurrentUserMutation } from '../../lib/core/mutations';
import { useRequiredUserInputFields } from '../../lib/core/requiredUserInputFieldsQuery';
import { useWizardContext } from '../../components/Wizard';
import { useMutation, ApolloError, gql } from '@apollo/client';
import { TermsStep } from '../TermsStep';
import { useSystem } from '../../lib/core/useSystem';
import { AddOrReplacePaymentMethod } from '../PaymentMethod/AddOrReplacePaymentMethod';
import { userFields } from '../Profile/messages';
import * as Yup from 'yup';
import { iconSize } from '../../lib/constants';

const sponsorsPath = '../../static/assets/sponsors';

interface BirthYearValues {
  birthYear: string;
}

function validate(values: BirthYearValues) {
  const errors: FormikErrors<BirthYearValues> = {};
  if (values.birthYear && values.birthYear.length !== 4) {
    errors.birthYear = 'Enter a valid birth year';
  }
  return errors;
}

const CONNECT_OBOS_MEMBERSHIP_MUTATION = gql`
  mutation connectObosMembershipMutation {
    connectObosMembership {
      id
    }
  }
`;

const StepLabel: FC<{ htmlFor: string }> = props => {
  return (
    <Label
      style={{
        lineHeight: 'inherit',
        fontSize: 'inherit',
        color: 'inherit'
      }}
      {...props}
    />
  );
};

function StepLayout({
  heading,
  icon,
  children,
  graphQLError,
  error,
  canSkip,
  onSubmit
}: {
  heading: React.ReactNode;
  icon: React.ReactElement;
  children: React.ReactNode;
  graphQLError?: ApolloError;
  error?: string;
  canSkip: boolean;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  const { goNext } = useWizardContext();
  return (
    <form onSubmit={onSubmit}>
      <Flex
        mx="auto"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
      >
        <Box mb="xs">{icon || <Logo color="primary" />}</Box>
        <H1 typoStyle="lg">{heading}</H1>
        {graphQLError && <GraphQLErrors error={graphQLError} />}
        {error && <Alert variant="error">{error}</Alert>}
        {children}
        {canSkip && (
          <Box mb="sm">
            <AnchorButton onClick={() => goNext()}>
              <FormattedMessage {...messages.layout.skip} />
            </AnchorButton>
          </Box>
        )}
      </Flex>
    </form>
  );
}

interface StepProps {
  currentUser: CurrentUser;
  required: boolean;
}

export function NameForm({ currentUser, required }: StepProps) {
  type Values = {
    name: string;
  };
  const intl = useIntl();
  const { goNext } = useWizardContext();
  const [updateCurrentUser, { error, loading }] = useUpdateCurrentUserMutation({
    onCompleted: () => {
      goNext();
    }
  });
  const fieldName = 'name';
  return (
    <Formik<Values>
      initialValues={{ name: currentUser.name || '' }}
      onSubmit={values => {
        updateCurrentUser({
          variables: {
            input: {
              name: values.name
            }
          }
        });
      }}
    >
      {({ handleSubmit }) => {
        return (
          <StepLayout
            heading={
              <StepLabel htmlFor={fieldName}>
                <FormattedMessage {...messages.nameForm.heading} />
              </StepLabel>
            }
            graphQLError={error}
            onSubmit={handleSubmit}
            icon={<ChatIcon size={iconSize} color="primaryIcon" />}
            canSkip={!required}
          >
            <Box mb="sm">
              <FormikField name={fieldName}>
                {({ field }: FieldProps<Values['name']>) => {
                  const label = intl.formatMessage(messages.nameForm.label);
                  return (
                    <InputInlineButton
                      inputProps={{
                        id: field.name,
                        placeholder: label,
                        ...field,
                        'data-testid': 'Onboarding_Name_Input'
                      }}
                      buttonProps={{
                        type: 'submit',
                        'data-testid': 'Onboarding_Name_Button',
                        'aria-label': intl.formatMessage(globalMessages.save)
                      }}
                      state={loading ? 'loading' : 'default'}
                    />
                  );
                }}
              </FormikField>
            </Box>
            <Text as="p" mute>
              <FormattedMessage {...messages.nameForm.description} />
            </Text>
          </StepLayout>
        );
      }}
    </Formik>
  );
}

export function BirthYearForm({ currentUser, required }: StepProps) {
  const { goNext } = useWizardContext();
  const [updateCurrentUser, { error, loading }] = useUpdateCurrentUserMutation({
    onCompleted: () => {
      goNext();
    }
  });
  return (
    <Formik<BirthYearValues>
      initialValues={{ birthYear: currentUser.birthYear || '' }}
      validate={validate}
      onSubmit={values => {
        updateCurrentUser({
          variables: {
            input: {
              birthYear: values.birthYear || null
            }
          }
        });
      }}
    >
      {({ handleSubmit, errors, touched, submitCount }) => {
        const fieldName = 'birthYear';
        const fieldError =
          submitCount > 0 && touched[fieldName] && errors[fieldName]
            ? errors[fieldName]
            : undefined;
        return (
          <StepLayout
            heading={
              <StepLabel htmlFor={fieldName}>
                <FormattedMessage {...messages.birthYearForm.heading} />
              </StepLabel>
            }
            error={fieldError}
            graphQLError={error}
            onSubmit={handleSubmit}
            icon={<CalendarIcon size={iconSize} color="primaryIcon" />}
            canSkip={!required}
          >
            <Box mb="sm">
              <FormikField name={fieldName}>
                {({ field }: FieldProps<BirthYearValues['birthYear']>) => {
                  return (
                    <InputInlineButton
                      inputProps={{
                        ...field,
                        id: field.name,
                        type: 'tel',
                        placeholder: '1900',
                        maxLength: 4,
                        width: '8rem',
                        'data-testid': 'Onboarding_Birth_Input'
                      }}
                      buttonProps={{
                        type: 'submit',
                        'data-testid': 'Onboarding_Birth_Button'
                      }}
                      state={loading ? 'loading' : 'default'}
                    />
                  );
                }}
              </FormikField>
            </Box>
            <Box>
              <Text as="p" mute>
                <FormattedMessage {...messages.birthYearForm.description} />
              </Text>
            </Box>
          </StepLayout>
        );
      }}
    </Formik>
  );
}

export function GenderForm({ currentUser, required }: StepProps) {
  type Values = { gender: Gender | null };
  const { goNext } = useWizardContext();
  const [updateCurrentUser, { error, loading }] = useUpdateCurrentUserMutation({
    onCompleted: () => {
      goNext();
    }
  });
  return (
    <Formik<Values>
      initialValues={{ gender: currentUser.gender || null }}
      onSubmit={values => {
        updateCurrentUser({
          variables: {
            input: {
              gender: values.gender
            }
          }
        });
      }}
    >
      {({ handleSubmit, setFieldValue, submitForm, values }) => {
        function onClick(gender: Gender | null) {
          setFieldValue('gender', gender);
          setTimeout(submitForm, 0);
        }
        const fieldName = 'gender';
        return (
          <StepLayout
            heading={
              <StepLabel htmlFor={fieldName}>
                <FormattedMessage {...messages.genderForm.heading} />
              </StepLabel>
            }
            graphQLError={error}
            onSubmit={handleSubmit}
            icon={<ProfileIcon size={iconSize} color="primaryIcon" />}
            canSkip={!required}
          >
            <Box mb="sm">
              <ButtonGroup segment>
                <Button
                  shape="cool"
                  variant={
                    values.gender === Gender.female ? 'primary' : undefined
                  }
                  disabled={loading}
                  onClick={() => onClick(Gender.female)}
                >
                  <FormattedMessage {...genders.female} />
                </Button>
                <Button
                  shape="cool"
                  variant={
                    values.gender === Gender.male ? 'primary' : undefined
                  }
                  disabled={loading}
                  onClick={() => onClick(Gender.male)}
                  data-testid="Onboarding_Gender_Male"
                >
                  <FormattedMessage {...genders.male} />
                </Button>
                <Button
                  shape="cool"
                  variant={
                    values.gender === Gender.other ? 'primary' : undefined
                  }
                  disabled={loading}
                  onClick={() => onClick(Gender.other)}
                >
                  <FormattedMessage {...genders.other} />
                </Button>
              </ButtonGroup>
            </Box>
            <Box>
              <Text as="p" mute>
                <FormattedMessage {...messages.genderForm.description} />
              </Text>
            </Box>
          </StepLayout>
        );
      }}
    </Formik>
  );
}

export function PostalCodeForm({ currentUser, required }: StepProps) {
  type Values = { postalCode: string };
  const intl = useIntl();
  const { goNext } = useWizardContext();
  const [updateCurrentUser, { error, loading }] = useUpdateCurrentUserMutation({
    onCompleted: () => {
      goNext();
    }
  });
  return (
    <Formik<Values>
      initialValues={{
        postalCode: currentUser.postalCode || ''
      }}
      onSubmit={values => {
        updateCurrentUser({
          variables: {
            input: {
              postalCode: values.postalCode
            }
          }
        });
      }}
    >
      {({ handleSubmit }) => {
        const fieldName = 'postalCode';
        return (
          <StepLayout
            heading={
              <StepLabel htmlFor={fieldName}>
                <FormattedMessage {...messages.postalCodeForm.heading} />
              </StepLabel>
            }
            graphQLError={error}
            onSubmit={handleSubmit}
            icon={<HomeIcon size={iconSize} color="primaryIcon" />}
            canSkip={!required}
          >
            <Box mb="sm">
              <FormikField name={fieldName}>
                {({ field }: FieldProps<Values['postalCode']>) => {
                  const label = intl.formatMessage(
                    messages.postalCodeForm.label
                  );
                  return (
                    <InputInlineButton
                      inputProps={{
                        id: fieldName,
                        placeholder: label,
                        ...field,
                        'data-testid': 'Onboarding_Postal_Input'
                      }}
                      buttonProps={{
                        type: 'submit',
                        'data-testid': 'Onboarding_Postal_Button'
                      }}
                      state={loading ? 'loading' : 'default'}
                    />
                  );
                }}
              </FormikField>
            </Box>
            <Box>
              <Text as="p" mute>
                <FormattedMessage {...messages.postalCodeForm.description} />
              </Text>
            </Box>
          </StepLayout>
        );
      }}
    </Formik>
  );
}

export function EmailForm({ currentUser, required }: StepProps) {
  type Values = {
    email: string;
    agreedToReceiveEmail: boolean;
    agreedToReceiveExternalPromotions: boolean;
  };
  const intl = useIntl();
  const { goNext } = useWizardContext();
  const requiredUserInputResult = useRequiredUserInputFields();
  const emailIsRequired =
    requiredUserInputResult?.data?.requiredUserInputFields?.email;

  const [updateCurrentUser, { error, loading }] = useUpdateCurrentUserMutation({
    onCompleted: () => {
      goNext();
    }
  });

  const validationSchema = Yup.object().shape({
    email: Yup.string().email()
  });

  return (
    <Formik<Values>
      initialValues={{
        email: currentUser.email || '',
        agreedToReceiveEmail: Boolean(currentUser.agreedToReceiveEmail),
        agreedToReceiveExternalPromotions: Boolean(
          currentUser.agreedToReceiveExternalPromotions
        )
      }}
      validationSchema={validationSchema}
      validate={() => {
        const errors: FormikErrors<Values> = {};

        return errors;
      }}
      onSubmit={values => {
        updateCurrentUser({
          variables: {
            input: {
              email: values.email
            }
          }
        });
      }}
    >
      {({ values, handleSubmit, errors, touched }) => {
        const fieldName = 'email';
        return (
          <StepLayout
            heading={<FormattedMessage {...messages.emailForm.heading} />}
            graphQLError={error}
            onSubmit={handleSubmit}
            icon={<MailIcon size={iconSize} color="primaryIcon" />}
            canSkip={!required}
          >
            {touched.email && errors.email && (
              <Alert variant="error">
                <FormattedMessage {...messages.emailForm.emailRequired} />
              </Alert>
            )}
            <Box mb="sm">
              <FormikField name="email">
                {({ field }: FieldProps<Values['email'], Values>) => {
                  const label = intl.formatMessage(messages.emailForm.label);
                  return (
                    <>
                      <Hide all srOnly>
                        <Label htmlFor={fieldName}>{label}</Label>
                      </Hide>
                      <Input
                        {...field}
                        id={fieldName}
                        data-testid="Onboarding_Email_Input"
                        required={emailIsRequired}
                        placeholder={label}
                        autoComplete="email"
                        type="email"
                      />
                    </>
                  );
                }}
              </FormikField>
            </Box>
            <Box>
              <Text as="p" mute>
                <FormattedMessage {...messages.emailForm.description} />{' '}
                {emailIsRequired && (
                  <FormattedMessage {...messages.emailForm.emailRequired} />
                )}
                {!emailIsRequired && (
                  <FormattedMessage {...messages.emailForm.emailNotRequired} />
                )}
              </Text>
            </Box>
            <Box mb="xs">
              <FormikField name="agreedToReceiveEmail">
                {({
                  field
                }: FieldProps<Values['agreedToReceiveEmail'], Values>) => {
                  return (
                    <CheckboxStateless
                      name={field.name}
                      onChange={field.onChange}
                      checked={field.value}
                      label={
                        <FormattedMessage
                          {...userFields.agreedToReceiveEmail}
                        />
                      }
                      disabled={loading}
                    />
                  );
                }}
              </FormikField>
            </Box>
            <Box mb="xs">
              <FormikField name="agreedToReceiveExternalPromotions">
                {({
                  field
                }: FieldProps<
                  Values['agreedToReceiveExternalPromotions'],
                  Values
                >) => {
                  return (
                    <CheckboxStateless
                      name={field.name}
                      onChange={field.onChange}
                      checked={field.value}
                      label={
                        <FormattedMessage
                          {...userFields.agreedToReceiveExternalPromotions}
                        />
                      }
                      disabled={loading}
                    />
                  );
                }}
              </FormikField>
            </Box>
            <LoadableButton
              data-testid="Onboarding_Email_Button"
              shape="cool"
              variant="primary"
              mb="xs"
              responseState={error ? 'error' : 'success'}
              loading={loading}
              disabled={errors.email ? true : false}
              onClick={() => {
                updateCurrentUser({
                  variables: {
                    input: values
                  }
                });
              }}
            >
              <FormattedMessage {...globalMessages.save} />
            </LoadableButton>
          </StepLayout>
        );
      }}
    </Formik>
  );
}

export function ConnectObosForm({ required }: StepProps) {
  const { goNext } = useWizardContext();
  const [connectMembership, result] = useMutation<
    connectObosMembershipMutation
  >(CONNECT_OBOS_MEMBERSHIP_MUTATION, {
    onCompleted: data => {
      if (data.connectObosMembership) {
        goNext();
      }
    },
    onError: () => {
      return;
    }
  });

  const firstGraphQLError = result.error?.graphQLErrors?.[0] as
    | GraphQLErrorWithCode
    | undefined;
  const membershipUnknown =
    firstGraphQLError?.code === 'ERR_OBOS_MEMBERSHIP_PHONE_NUMBER_UNKNOWN';

  return (
    <StepLayout
      heading="Som OBOS-medlem får du egne fordeler i appen"
      graphQLError={result.error}
      onSubmit={event => {
        event.preventDefault();
        connectMembership();
      }}
      icon={
        <Box size={iconSize}>
          <Image src={`${sponsorsPath}/obos-default.svg`} width="100%" />
        </Box>
      }
      canSkip={!required}
    >
      <Text as="p" mute>
        For å sjekke om du er medlem sendes ditt mobilnummer til OBOS. Nummeret
        blir ikke brukt til noe annet formål enn verifisering. OBOS er
        bysykkelpartner i Oslo.
      </Text>
      <LoadableButton
        type="submit"
        variant="primary"
        shape="cool"
        mb="sm"
        mt="xs"
        loading={result.loading}
        responseState={result.error ? 'error' : 'success'}
      >
        Koble ditt OBOS-medlemskap
      </LoadableButton>
      {membershipUnknown && (
        <Box mb="sm">
          <WarningIcon
            color="primaryIcon"
            size={30}
            display="block"
            ml="auto"
            mr="auto"
          />
          <Text mt="xs">
            <FormattedMessage
              {...messages.obosStep.noObos}
              values={{
                obosLink: (
                  <Anchor href="https://minside.obos.no">
                    minside.obos.no
                  </Anchor>
                )
              }}
            />
          </Text>
        </Box>
      )}
    </StepLayout>
  );
}

export const AgreedToTermsForm = () => {
  const { goNext } = useWizardContext();
  return <TermsStep goNext={goNext} />;
};

function HavePaymentMethod({
  systemName,
  paymentMethod,
  goNext
}: {
  systemName: string;
  paymentMethod?: CurrentUser_paymentMethod | null;
  goNext: () => void;
}) {
  return (
    <>
      {paymentMethod &&
        paymentMethod.__typename === 'Card' &&
        !paymentMethod.expired && (
          <Text as="p">
            <FormattedMessage
              id="PaymentMethodForm.paymentCardDescription"
              defaultMessage="We now have your {displayBrand} ending with {last4} saved to your {systemName} account."
              values={{
                displayBrand: paymentMethod.displayBrand,
                last4: paymentMethod.last4,
                systemName
              }}
            />
          </Text>
        )}

      {paymentMethod &&
        paymentMethod.__typename === 'Card' &&
        !paymentMethod.expired && (
          <>
            <Text as="p">
              <FormattedMessage
                id="PaymentMethodForm.paymentCardProfileInfo"
                defaultMessage="You can remove and add cards at any time in your profile."
              />
            </Text>
            <Button shape="cool" variant="primary" onClick={goNext}>
              <I18NContinue />
            </Button>
          </>
        )}
    </>
  );
}

export const PaymentMethodForm = ({ currentUser, required }: StepProps) => {
  const { goNext } = useWizardContext();
  const system = useSystem({
    onCompleted: data => {
      if (!data.system?.paymentProvider) {
        goNext();
      }
    }
  });

  const component = (
    <AddOrReplacePaymentMethod>
      {({ addOrReplacePaymentMethodUI, paymentMethodSuccess }) => {
        if (paymentMethodSuccess) {
          const paymentMethod = currentUser?.paymentMethod;
          return (
            <HavePaymentMethod
              systemName={system.name}
              paymentMethod={paymentMethod}
              goNext={goNext}
            />
          );
        }
        return addOrReplacePaymentMethodUI;
      }}
    </AddOrReplacePaymentMethod>
  );
  let paymentProviderName;

  const { paymentProvider } = system;

  if (!paymentProvider) {
    return null;
  }

  const { paymentMethod } = currentUser;
  const userHavePaymentMethod = Boolean(currentUser.paymentMethod);
  const paymentMethodIsExpired =
    currentUser.paymentMethod &&
    currentUser.paymentMethod.__typename === 'Card' &&
    currentUser.paymentMethod.expired;

  switch (paymentProvider.__typename) {
    case 'PaymentProviderPayex': {
      paymentProviderName = 'PayEx';
      break;
    }
    case 'PaymentProviderStripe': {
      paymentProviderName = 'Stripe';
      break;
    }
  }

  const mustAddPaymentCard = !userHavePaymentMethod || paymentMethodIsExpired;

  return (
    <StepLayout
      heading={
        mustAddPaymentCard ? (
          <FormattedMessage
            id="PaymentMethodForm.addPaymentCard"
            defaultMessage="Add payment card"
          />
        ) : (
          <FormattedMessage
            id="PaymentMethodForm.paymentCardAdded"
            defaultMessage="Card added successfully!"
          />
        )
      }
      icon={<Creditcard size={iconSize} color="primaryIcon" />}
      canSkip={!required && !mustAddPaymentCard}
    >
      {mustAddPaymentCard && (
        <>
          {paymentMethodIsExpired &&
            paymentMethod &&
            paymentMethod.__typename === 'Card' && (
              <Text as="p">
                <FormattedMessage
                  id="PaymentMethodForm.cardExpired"
                  defaultMessage="Your card ending with {last4} is expired. Please add a new card."
                  values={{ last4: paymentMethod.last4 }}
                />
              </Text>
            )}
          <Text as="p">
            <FormattedMessage
              id="PaymentMethodForm.description"
              defaultMessage="To be able to unlock our bikes, you need to add a valid payment card. Adding the card will not charge your card. You can add and remove cards at any time in your profile."
            />
          </Text>
          <Text as="p">
            <FormattedMessage
              id="PaymentMethodForm.paymentMethodDescription"
              defaultMessage="Selecting {addCard} will take you to our payment provider {paymentProviderName}"
              values={{
                paymentProviderName,
                addCard: <FormattedMessage {...globalMessages.addCard} />
              }}
            />
          </Text>
        </>
      )}
      <Box mb="sm" data-testid="PaymentMethodStep">
        {component}
      </Box>
    </StepLayout>
  );
};
